import React from 'react'
import ProgramEditSection from 'features/Programs/Editing/Components/ProgramEditSection'
import { ProgramsType } from '../Shared/types'
import QtfProgramEditing from './QtfProgramEditing'
import GrhProgramEditing from './GrhProgramEditing'
import MicromobilityProgramEditing from './MicromobilityProgramEditing'
import FlexProgramEditing from './FlexProgramEditing'
import TransitPassProgramEditing from './TransitPassProgramEditing'
import CouponProgramEditing from './CouponProgramEditing'
import ReportedUsageProgramEditing from './ReportedUsageProgramEditing'
import { Flex, Micromobility, QTF } from 'core-system/Icons/Sidebar'
import { Car } from 'core-system/Icons/modeTypes'
import CreditCard from 'core-system/Icons/Accounts/CreditCard'
import Coupon from 'core-system/Icons/Programs/Coupon'
import Insights from 'core-system/Icons/Sidebar/Insights'
import {
  grhType,
  micromobilityType,
  flexType,
  qtfType,
  transitPassType,
  couponType,
  availableGrhMerchant,
  availableMicromobilityMerchant,
  availableFlexMerchant,
  qtfBenefitsType,
  qtfPayrollScheduleType,
  grhActiveOptionsType,
  grhTransactionUsesType,
  micromobilityActiveOptionsType,
  flexActiveOptionsType,
  BudgetCycle,
  TransitPassPurchaseType,
  TransitPassDeliveryMechanism,
} from '../Shared/types'
import { Segment } from 'redux/employer/employerTypes'
import { Month } from 'shared/DateUtils'

interface ProgramsEditingViewProps {
  isContinued: boolean
  grh: grhType
  qtf: qtfType
  micromobility: micromobilityType
  flex: flexType
  transitPass: transitPassType
  coupon: couponType
  allSegments: Segment[]
  irsTaxLimit: number
  selectedPrograms: ProgramsType[]
  selectedDate: Month
  availableGrhMerchant: availableGrhMerchant
  availableMicromobilityMerchant: availableMicromobilityMerchant
  availableFlexMerchant: availableFlexMerchant
  changeQtfBenefits: (type: qtfBenefitsType, value: boolean) => void
  changeQtfEmployerContributions: (type: qtfBenefitsType, value: number) => void
  changeQtfFinalizeAllocation: (day: number) => void
  changeQtfPayrollSchedule: (payrollSchedule: qtfPayrollScheduleType) => void
  changeQtfMaxAllocationLimit: (type: qtfBenefitsType, limit: number) => void
  getGrhRecommendations: ({ segmentId }: { segmentId: string }) => void
  changeGrhActiveMerchantIds: (
    type: grhActiveOptionsType,
    value: string[]
  ) => void
  changeGrhTransactionUses: (
    type: grhTransactionUsesType,
    value: number
  ) => void
  changeGrhActiveOptions: (type: grhActiveOptionsType, value: boolean) => void
  getMicromobilityRecommendations: (segmentId: string) => void
  changeMicromobilityActiveMerchantIds: (
    type: micromobilityActiveOptionsType,
    value: string[]
  ) => void
  changeMicromobilityActiveOptions: (
    type: micromobilityActiveOptionsType,
    value: boolean
  ) => void
  changeMicromobilityBudget: (budget: number) => void
  changeMicromobilityProrated: (value: boolean) => void
  getFlexRecommendations: (segmentId: string) => void
  changeFlexActiveMerchantIds: (
    type: flexActiveOptionsType,
    value: string[]
  ) => void
  changeFlexActiveOptions: (type: flexActiveOptionsType, value: boolean) => void
  changeFlexBudget: (budget: number) => void
  changeFlexBudgetCycle: (cycle: BudgetCycle) => void
  changeFlexOneTimePurchase: (value: boolean) => void
  changeFlexRolloverBudget: (value: boolean) => void
  changeFlexProrated: (value: boolean) => void
  changeTransitPassPurchaseType: (value: TransitPassPurchaseType) => void
  changeTransitPassWalletTopUpEnabled: (value: boolean) => void
  changeTransitPassBudget: (budget: number) => void
  changeTransitPassDeliveryMechanism: (
    value: TransitPassDeliveryMechanism
  ) => void
  changeTransitPassAgencyIds: (value: string[]) => void
  changeTransitPassAgenciesEnabled: (value: boolean) => void
  getTransitAgencies: () => void
  changeCouponMerchantIds: (value: string[]) => void
  changeCouponMerchantsEnabled: (value: boolean) => void
  getCouponMerchants: () => void
  changeCouponCode: (value: string) => void
  canEditAllocationsDate: boolean
  getGovernmentGrhPrograms: () => void
}

const ProgramsEditingView = ({
  isContinued,
  grh,
  qtf,
  micromobility,
  flex,
  transitPass,
  coupon,
  allSegments,
  irsTaxLimit,
  selectedPrograms,
  selectedDate,
  availableGrhMerchant,
  availableMicromobilityMerchant,
  availableFlexMerchant,
  changeQtfBenefits,
  changeQtfEmployerContributions,
  changeQtfFinalizeAllocation,
  changeQtfPayrollSchedule,
  changeQtfMaxAllocationLimit,
  getGrhRecommendations,
  changeGrhActiveMerchantIds,
  changeGrhTransactionUses,
  changeGrhActiveOptions,
  getMicromobilityRecommendations,
  changeMicromobilityActiveMerchantIds,
  changeMicromobilityActiveOptions,
  changeMicromobilityBudget,
  changeMicromobilityProrated,
  getFlexRecommendations,
  changeFlexActiveMerchantIds,
  changeFlexActiveOptions,
  changeFlexBudget,
  changeFlexBudgetCycle,
  changeFlexOneTimePurchase,
  changeFlexRolloverBudget,
  changeFlexProrated,
  changeTransitPassPurchaseType,
  changeTransitPassWalletTopUpEnabled,
  changeTransitPassBudget,
  changeTransitPassDeliveryMechanism,
  changeTransitPassAgencyIds,
  changeTransitPassAgenciesEnabled,
  getTransitAgencies,
  changeCouponMerchantIds,
  changeCouponMerchantsEnabled,
  getCouponMerchants,
  changeCouponCode,
  canEditAllocationsDate,
  getGovernmentGrhPrograms,
}: ProgramsEditingViewProps) => {
  const renderProgram = (program: ProgramsType) => {
    switch (program) {
      case 'qtf':
        return (
          <QtfProgramEditing
            qtf={qtf}
            irsTaxLimit={irsTaxLimit}
            selectedDate={selectedDate}
            changeQtfBenefits={changeQtfBenefits}
            changeQtfEmployerContributions={changeQtfEmployerContributions}
            changeQtfFinalizeAllocation={changeQtfFinalizeAllocation}
            changeQtfPayrollSchedule={changeQtfPayrollSchedule}
            changeQtfMaxAllocationLimit={changeQtfMaxAllocationLimit}
            canEditAllocationsDate={canEditAllocationsDate}
          />
        )
      case 'grh':
        return (
          <GrhProgramEditing
            grh={grh}
            availableMerchants={availableGrhMerchant}
            getGrhRecommendations={getGrhRecommendations}
            changeGrhActiveMerchantIds={changeGrhActiveMerchantIds}
            changeGrhTransactionUses={changeGrhTransactionUses}
            changeGrhActiveOptions={changeGrhActiveOptions}
            getGovernmentGrhPrograms={getGovernmentGrhPrograms}
          />
        )
      case 'micromobility':
        return (
          <MicromobilityProgramEditing
            micromobility={micromobility}
            availableMerchants={availableMicromobilityMerchant}
            getMicromobilityRecommendations={getMicromobilityRecommendations}
            changeMicromobilityActiveMerchantIds={
              changeMicromobilityActiveMerchantIds
            }
            changeMicromobilityActiveOptions={changeMicromobilityActiveOptions}
            selectedDate={selectedDate}
            isContinued={isContinued}
            allSegments={allSegments}
            changeMicromobilityBudget={changeMicromobilityBudget}
            changeMicromobilityProrated={changeMicromobilityProrated}
          />
        )
      case 'flex':
        return (
          <FlexProgramEditing
            flex={flex}
            availableMerchants={availableFlexMerchant}
            getFlexRecommendations={getFlexRecommendations}
            changeFlexActiveMerchantIds={changeFlexActiveMerchantIds}
            changeFlexActiveOptions={changeFlexActiveOptions}
            changeFlexBudget={changeFlexBudget}
            changeFlexBudgetCycle={changeFlexBudgetCycle}
            changeFlexOneTimePurchase={changeFlexOneTimePurchase}
            changeFlexRolloverBudget={changeFlexRolloverBudget}
            changeFlexProrated={changeFlexProrated}
            isContinued={isContinued}
            allSegments={allSegments}
          />
        )
      case 'transitPass':
        return (
          <TransitPassProgramEditing
            transitPass={transitPass}
            changePurchaseType={changeTransitPassPurchaseType}
            changeWalletTopUpEnabled={changeTransitPassWalletTopUpEnabled}
            changeBudget={changeTransitPassBudget}
            changeDeliveryMechanism={changeTransitPassDeliveryMechanism}
            changeAgencyIds={changeTransitPassAgencyIds}
            changeAgenciesEnabled={changeTransitPassAgenciesEnabled}
            getTransitAgencies={getTransitAgencies}
          />
        )
      case 'coupon':
        return (
          <CouponProgramEditing
            coupon={coupon}
            changeMerchantIds={changeCouponMerchantIds}
            changeMerchantsEnabled={changeCouponMerchantsEnabled}
            getCouponMerchants={getCouponMerchants}
            changeCouponCode={changeCouponCode}
          />
        )
      case 'reportedUsage':
        return <ReportedUsageProgramEditing />
      default:
        return null
    }
  }

  const renderIcon = (program: ProgramsType) => {
    switch (program) {
      case 'qtf':
        return <QTF />
      case 'grh':
        return <Car />
      case 'micromobility':
        return <Micromobility />
      case 'flex':
        return <Flex />
      case 'transitPass':
        return <CreditCard />
      case 'coupon':
        return <Coupon />
      case 'reportedUsage':
        return <Insights />
    }
  }

  if (selectedPrograms.length === 0) {
    return null
  }

  return (
    <>
      {selectedPrograms.map((program: ProgramsType) => (
        <ProgramEditSection
          program={program}
          icon={renderIcon(program)}
          children={renderProgram(program)}
        />
      ))}
    </>
  )
}

export default React.memo(ProgramsEditingView)
